/**
 * The 24 levels: two per CAPS grade (early / late). A level = content band
 * (weighted template mix + difficulty range) + speed band (slack) + theme.
 *
 * TEMPLATE_CATALOGUE is the source of truth for template ids. Every id listed
 * here must exist in algebra/templates (enforced by test/levels.test.ts).
 * Ids are stored in run logs, so they are never renamed; a template that moves
 * to a later grade keeps its id (its `g<N>.` prefix is then historical).
 * Full descriptions and examples: docs/CONTENT_CATALOGUE.md.
 */
import { LEVEL_COUNT, LEVEL_WIDTH, PACE } from '../config.ts';
import type { LevelDef } from '../types.ts';

export const TEMPLATE_CATALOGUE: Record<number, { early: string[]; late: string[] }> = {
  1: { early: ['g1.add-missing-end', 'g1.add-missing-start'], late: ['g1.sub-missing', 'g1.add-to-20'] },
  2: { early: ['g2.add-missing-20', 'g2.sub-missing-20', 'g2.balance-add', 'g2.skip-count'], late: ['g2.add-tens'] },
  3: { early: ['g3.mult-missing', 'g3.div-missing', 'g3.balance-mixed', 'g3.pattern'], late: ['g3.add-sub-100'] },
  4: { early: ['g4.var-add', 'g4.var-mult', 'g4.var-div', 'g4.pattern-rule'], late: ['g4.bracket-missing'] },
  5: { early: ['g5.var-sub', 'g5.coef-mult', 'g5.flow-rule', 'g5.two-op-missing'], late: ['g5.pattern-term'] },
  6: {
    early: ['g6.substitute', 'g6.frac-eq', 'g6.two-step', 'g6.two-step-sub'],
    late: ['g6.substitute-two'],
  },
  7: {
    early: ['g7.two-step-neg', 'g7.frac-two-step', 'g7.substitute-neg', 'g7.exponent-eval'],
    late: ['g7.both-sides-simple'],
  },
  // Like terms are CAPS Grade 8 content: g6.like-terms-coef and g7.like-terms-mixed keep their
  // original ids (stored in run logs) but are placed, and graded, here.
  8: {
    early: [
      'g8.both-sides', 'g8.brackets', 'g8.exp-law-mult', 'g8.exp-law-power', 'g8.exp-law-div', 'g8.expand-coef',
      'g6.like-terms-coef',
    ],
    late: ['g8.brackets-both', 'g8.simplify-coef', 'g7.like-terms-mixed'],
  },
  9: {
    early: ['g9.expand-binomial', 'g9.expand-binomial-neg', 'g9.factor-common', 'g9.eq-fractions', 'g9.exp-negative', 'g9.exp-mixed'],
    late: ['g9.linear-pattern', 'g9.square-expand'],
  },
  10: {
    early: ['g10.factor-trinomial', 'g10.factor-trinomial-neg', 'g10.diff-squares', 'g10.quadratic-root', 'g10.simultaneous', 'g10.exponential-eq'],
    late: ['g10.inequality'],
  },
  11: {
    early: ['g11.quadratic-a', 'g11.surd-simplify', 'g11.exp-eq-bases', 'g11.discriminant', 'g11.arith-term', 'g11.quad-sequence'],
    late: ['g11.complete-square', 'g11.exp-rational'],
  },
  12: {
    early: ['g12.log-eval', 'g12.log-eq', 'g12.log-laws', 'g12.geo-term', 'g12.arith-series', 'g12.geo-series', 'g12.function-eval', 'g12.inverse-eval'],
    late: ['g12.sigma'],
  },
};

/** Theme id per level (index 0 = level 1). Theme data: themes/themes.ts. */
export const LEVEL_THEMES = [
  'sunny-meadow', 'jungle-canopy', 'parrot-cove', 'waterfall-gorge', 'mushroom-glade', 'savanna-sunrise',
  'coral-lagoon', 'canyon-rapids', 'sky-islands', 'balloon-festival', 'crystal-grotto', 'desert-oasis',
  'bamboo-steps', 'volcano-island', 'glacier-bay', 'candy-cliffs', 'pirate-harbour', 'treetop-village',
  'cloud-castle', 'jungle-ruins', 'rainbow-falls', 'wild-coast', 'fynbos-peaks', 'sun-temple-summit',
] as const;

const CURRENT_WEIGHT = 3;
const REVIEW_WEIGHT_EARLY = 1;
const REVIEW_WEIGHT_LATE = 0.5;

function buildLevel(index: number): LevelDef {
  const grade = Math.ceil(index / 2);
  const stage = index % 2 === 1 ? 'early' : 'late';
  const cat = TEMPLATE_CATALOGUE[grade]!;
  const prev = TEMPLATE_CATALOGUE[grade - 1];
  const templates: { id: string; weight: number }[] = [];
  if (stage === 'early') {
    for (const id of cat.early) templates.push({ id, weight: CURRENT_WEIGHT });
    if (prev) for (const id of [...prev.early, ...prev.late]) templates.push({ id, weight: REVIEW_WEIGHT_EARLY });
  } else {
    for (const id of [...cat.early, ...cat.late]) templates.push({ id, weight: CURRENT_WEIGHT });
    if (prev) for (const id of [...prev.early, ...prev.late]) templates.push({ id, weight: REVIEW_WEIGHT_LATE });
  }
  const t = (index - 1) / (LEVEL_COUNT - 1);
  return {
    index,
    grade,
    stage,
    difficultyIndex: Math.round((1 + 5 * t) * 1000) / 1000,
    templates,
    templateDifficulty: stage === 'early' ? [0, 0.55] : [0.35, 1],
    slack: Math.round((PACE.SLACK_FIRST - (PACE.SLACK_FIRST - PACE.SLACK_LAST) * t) * 1000) / 1000,
    themeId: LEVEL_THEMES[index - 1]!,
  };
}

export const LEVELS: readonly LevelDef[] = Array.from({ length: LEVEL_COUNT }, (_, i) => buildLevel(i + 1));

export function getLevel(index: number): LevelDef {
  const l = LEVELS[Math.min(LEVEL_COUNT, Math.max(1, Math.round(index))) - 1];
  if (!l) throw new Error(`no level ${index}`);
  return l;
}

/** Lowest rating that maps to this level. */
export function ratingForLevel(level: number): number {
  return (Math.min(LEVEL_COUNT, Math.max(1, level)) - 1) * LEVEL_WIDTH;
}
